import {
  Button,
  Textarea,
  Card,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Input,
  Label,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Spinner,
} from '@design-system';
import { Trash2, X, Plus, Users, User, Info, Edit3 } from 'lucide-react';
import { usePuiEmailReview } from '../hooks/use-pui-email-review';
import { useState } from 'react';

interface PuiEmailReviewProps {
  intakeSections: FormattedIntakeSection[];
  partnerIntakeSections?: FormattedIntakeSection[];
  products: string[];
  onSend: (data: SendEmailParams) => void;
  onCancel: () => void;
  isPending?: boolean;
}

export function PuiEmailReview({
  intakeSections: initialIntakeSections,
  partnerIntakeSections: initialPartnerIntakeSections,
  products,
  onSend,
  onCancel,
  isPending = false,
}: PuiEmailReviewProps) {
  const [showAdvancedEditing, setShowAdvancedEditing] = useState(false);

  const {
    anonymousIdentifier,
    setAnonymousIdentifier,
    intakeSections,
    partnerIntakeSections,
    customMessages,
    setCustomMessages,
    hasPartner,
    productName,
    carrierCount,
    carrierText,
    individualPreviewHtml,
    jointPreviewHtml,
    handleDeleteSection,
    handleDeleteItem,
    handleEditItemValue,
    handleEditItemLabel,
    handleEditSectionCategory,
    handleAddItem,
    handleAddSection,
  } = usePuiEmailReview({
    intakeSections: initialIntakeSections,
    partnerIntakeSections: initialPartnerIntakeSections,
    products,
  });

  const handleSend = () => {
    if (!anonymousIdentifier.trim()) {
      return;
    }
    onSend({
      anonymousIdentifier,
      intakeSections,
      partnerIntakeSections,
      customMessages: Object.keys(customMessages).length > 0 ? customMessages : undefined,
    });
  };

  const isAnonymousIdValid = anonymousIdentifier.trim().length > 0;

  const renderSection = (
    section: FormattedIntakeSection,
    sectionIndex: number,
    isPartner: boolean = false,
  ) => {
    return (
      <Card
        key={sectionIndex}
        className="mb-3 p-4"
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="mb-1 flex items-center gap-2">
              <Label className="text-muted-foreground text-xs">Section Header</Label>
            </div>
            <Input
              value={section.category}
              onChange={e => handleEditSectionCategory(sectionIndex, e.target.value, isPartner)}
              className="font-semibold"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDeleteSection(sectionIndex, isPartner)}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive mt-5 h-8 w-8"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-2">
          {section.items.map((item, itemIndex) => (
            <div
              key={itemIndex}
              className="flex items-start gap-2"
            >
              <div className="flex-1 space-y-1">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-muted-foreground text-xs">Label</Label>
                    <Input
                      value={item.label || ''}
                      onChange={e =>
                        handleEditItemLabel(sectionIndex, itemIndex, e.target.value, isPartner)
                      }
                      placeholder="Field label"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Value</Label>
                    <Input
                      value={item.value}
                      onChange={e =>
                        handleEditItemValue(sectionIndex, itemIndex, e.target.value, isPartner)
                      }
                      placeholder="Field value"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDeleteItem(sectionIndex, itemIndex, isPartner)}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive mt-5 h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => handleAddItem(sectionIndex, isPartner)}
          className="mt-3 w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Field
        </Button>
      </Card>
    );
  };

  return (
    <div className="bg-background relative flex h-[82vh] flex-col overflow-hidden">
      {/* Loading overlay */}
      {isPending && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <Spinner className="h-12 w-12 text-white" />
            <p className="text-sm font-medium text-white">Sending inquiry...</p>
          </div>
        </div>
      )}

      {/* <CardHeader className="border-b px-5 py-4">
        <CardTitle className="text-foreground text-lg">Review Template</CardTitle>
      </CardHeader> */}
      <div className="flex items-center justify-between border-b px-5 py-3">
        <h2 className="text-foreground text-base font-semibold">Review Template</h2>
      </div>

      <div
        className={`min-h-0 flex-1 ${showAdvancedEditing ? 'grid grid-cols-2 gap-0 divide-x' : 'flex flex-col'}`}
      >
        {/* Left - only show when editing */}
        {showAdvancedEditing && (
          <div className="min-h-0 overflow-y-auto p-4">
            <div className="mb-4">
              <h3 className="text-foreground text-sm font-semibold">Edit Email Content</h3>
            </div>

            <div className="space-y-4">
              {/* sections here */}
              {/* Anonymous ID Section */}
              <Card className="bg-muted/20 border p-4 shadow-none">
                <div>
                  <Label className="text-muted-foreground text-xs font-medium">
                    Anonymous Identifier <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={anonymousIdentifier}
                    onChange={e => setAnonymousIdentifier(e.target.value)}
                    placeholder="ANONYMOUS-ID-12345"
                    className="mt-1"
                    required
                  />
                  <p className="text-muted-foreground mt-1 text-xs">
                    This ID will be used in the email and for tracking responses
                  </p>
                </div>
              </Card>

              {/* Quoted Products Section */}
              <Card className="bg-muted/20 border p-4 shadow-none">
                <div>
                  <Label className="text-muted-foreground text-xs font-medium">
                    Quoted Products
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {products.map((product, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="text-sm"
                      >
                        {product}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {products.length > 1
                      ? `${products.length} products being quoted. Email Preview references "${productName}" as a placeholder.`
                      : 'Product being quoted in this request'}
                  </p>
                </div>
              </Card>

              {showAdvancedEditing && (
                <>
                  {/* Custom Messages Section */}
                  <Accordion
                    type="single"
                    collapsible
                    className="mb-4"
                  >
                    <AccordionItem value="messages">
                      <AccordionTrigger className="text-sm font-semibold">
                        Customize Messages
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          {/* Header Section */}
                          <div className="border-primary/20 rounded-md border p-3">
                            <h5 className="text-primary-dark mb-2 text-xs font-semibold uppercase">
                              Email Headers
                            </h5>
                            <div className="space-y-3">
                              <div>
                                <Label className="text-xs">Greeting</Label>
                                <Input
                                  placeholder="Dear Underwriting Team,"
                                  value={customMessages.greeting ?? ''}
                                  onChange={e =>
                                    setCustomMessages({
                                      ...customMessages,
                                      greeting: e.target.value,
                                    })
                                  }
                                />
                              </div>

                              <div>
                                <div className="mb-1 flex items-center gap-2">
                                  <Label className="text-xs">Introduction Paragraph</Label>
                                </div>
                                <Textarea
                                  placeholder="We are requesting a preliminary underwriting inquiry..."
                                  value={customMessages.introParagraph ?? ''}
                                  onChange={e =>
                                    setCustomMessages({
                                      ...customMessages,
                                      introParagraph: e.target.value,
                                    })
                                  }
                                  rows={3}
                                />
                                {hasPartner && (
                                  <p className="text-muted-foreground mt-1 text-xs">
                                    Note: For joint cases, the text will automatically include "and
                                    their partner" after "client"
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Footer Section */}
                          <div className="border-primary/20 rounded-md border p-3">
                            <h5 className="text-primary-dark mb-2 text-xs font-semibold uppercase">
                              Email Footers
                            </h5>
                            <div className="space-y-3">
                              <div>
                                <Label className="text-xs">Product Reference Note</Label>
                                <Input
                                  placeholder="Please reference this ID in all correspondence..."
                                  value={customMessages.productReferenceNote ?? ''}
                                  onChange={e =>
                                    setCustomMessages({
                                      ...customMessages,
                                      productReferenceNote: e.target.value,
                                    })
                                  }
                                />
                              </div>

                              <div>
                                <Label className="text-xs">Outro Request Intro</Label>
                                <Input
                                  placeholder="Please review this information and provide preliminary feedback on:"
                                  value={customMessages.outroRequestIntro ?? ''}
                                  onChange={e =>
                                    setCustomMessages({
                                      ...customMessages,
                                      outroRequestIntro: e.target.value,
                                    })
                                  }
                                />
                              </div>

                              <div>
                                <Label className="text-xs">Outro Closing Instruction</Label>
                                <Textarea
                                  placeholder={`Please reply with your preliminary assessment using the reference ID ${anonymousIdentifier}.`}
                                  value={customMessages.outroClosingInstruction ?? ''}
                                  onChange={e =>
                                    setCustomMessages({
                                      ...customMessages,
                                      outroClosingInstruction: e.target.value,
                                    })
                                  }
                                  rows={2}
                                />
                              </div>

                              <div>
                                <Label className="text-xs">Closing Greeting</Label>
                                <Input
                                  placeholder="Best regards,"
                                  value={customMessages.closingGreeting ?? ''}
                                  onChange={e =>
                                    setCustomMessages({
                                      ...customMessages,
                                      closingGreeting: e.target.value,
                                    })
                                  }
                                />
                              </div>

                              <div>
                                <Label className="text-xs">Closing Signature</Label>
                                <Input
                                  placeholder="Waterlily Team"
                                  value={customMessages.closingSignature ?? ''}
                                  onChange={e =>
                                    setCustomMessages({
                                      ...customMessages,
                                      closingSignature: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Client Information Sections */}
                  {hasPartner && partnerIntakeSections !== undefined ? (
                    <Accordion
                      type="single"
                      collapsible
                      defaultValue="primary"
                      className="mb-4"
                    >
                      <AccordionItem value="primary">
                        <AccordionTrigger className="text-sm font-semibold">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Primary Client Information
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="mb-2 flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddSection(false)}
                            >
                              <Plus className="mr-1 h-3 w-3" />
                              Add Section
                            </Button>
                          </div>
                          {intakeSections.map((section, index) =>
                            renderSection(section, index, false),
                          )}
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="partner">
                        <AccordionTrigger className="text-sm font-semibold">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Partner Information
                            <Badge
                              variant="secondary"
                              className="text-xs"
                            >
                              Joint cases only
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="text-muted-foreground mb-2 text-xs">
                            This section will only appear in joint/partner email templates
                          </p>
                          <div className="mb-2 flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddSection(true)}
                            >
                              <Plus className="mr-1 h-3 w-3" />
                              Add Section
                            </Button>
                          </div>
                          {partnerIntakeSections.map((section, index) =>
                            renderSection(section, index, true),
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <div className="mb-4">
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-foreground text-sm font-semibold">
                          Client Information
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddSection(false)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Section
                        </Button>
                      </div>
                      {intakeSections.map((section, index) => renderSection(section, index, false))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Right */}
        <div className={`min-h-0 overflow-y-auto p-4 ${showAdvancedEditing ? 'bg-muted/30' : ''}`}>
          <h3 className="text-foreground mb-4 text-sm font-semibold">Email Preview</h3>

          {hasPartner && jointPreviewHtml ? (
            <Tabs
              defaultValue="individual"
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger
                  value="individual"
                  className="flex items-center gap-1"
                >
                  <User className="h-4 w-4" />
                  Individual
                </TabsTrigger>
                <TabsTrigger
                  value="joint"
                  className="flex items-center gap-1"
                >
                  <Users className="h-4 w-4" />
                  Joint (with Partner)
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="individual"
                className="mt-4"
              >
                <div
                  className="rounded-lg bg-white p-1"
                  style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: individualPreviewHtml }}
                />
              </TabsContent>

              <TabsContent
                value="joint"
                className="mt-4"
              >
                <div
                  className="rounded-lg p-1"
                  style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: jointPreviewHtml }}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <div
              className="rounded-lg p-1"
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: individualPreviewHtml }}
            />
          )}
        </div>
      </div>

      <div className="bg-background sticky bottom-0 flex items-center justify-between border-t px-5 py-3">
        <Button
          onClick={onCancel}
          variant="outline"
          disabled={isPending}
        >
          Back to Product Selection
        </Button>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => setShowAdvancedEditing(!showAdvancedEditing)}
            variant="secondary"
            disabled={isPending}
            className="gap-2"
          >
            <Edit3 className="h-4 w-4" />
            {showAdvancedEditing ? 'Hide Editor' : 'Personalize Draft'}
          </Button>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handleSend}
                    disabled={isPending || !isAnonymousIdValid}
                    className="gap-2"
                  >
                    {isPending ? 'Sending...' : `Send Inquiry to ${carrierCount} ${carrierText}`}
                    <Info className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-1">
                  <p className="font-semibold">Sending to:</p>
                  {products.map((product, index) => (
                    <p
                      key={index}
                      className="text-sm"
                    >
                      • {product}
                    </p>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
